import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding, faHospital, faBan, faCheckCircle, faSearch } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { SUPER_ADMIN_NAV } from './nav';

interface Clinic { id: number; name: string; active: boolean; staff_count: number; }
interface Institution {
    id: number; name: string; email: string; active: boolean; town: string | null;
    clinic_count: number; staff_count: number; clinics: Clinic[];
}

const TenantManagement: React.FC = () => {
    const { api } = useInternalApi();
    const [tenants, setTenants] = useState<Institution[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [expanded, setExpanded] = useState<Set<number>>(new Set());

    useEffect(() => { document.title = 'Tenant & Org Management - Uzima Internal'; }, []);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (search) params.set('search', search);
            const res = await api.get(`platform/admin/tenants/?${params}`);
            if (res.data?.status === 'success') setTenants(res.data.data || []);
        } catch { setTenants([]); } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleTenant = async (type: 'institution' | 'clinic', id: number, active: boolean) => {
        const confirm = await Swal.fire({
            title: `${active ? 'Reactivate' : 'Suspend'} this ${type}?`, icon: 'warning', showCancelButton: true,
            confirmButtonColor: active ? '#0ab39c' : '#f06548',
        });
        if (!confirm.isConfirmed) return;
        try {
            await api.patch(`platform/admin/tenants/${type}/${id}/`, { active });
            load();
        } catch {
            Swal.fire({ icon: 'error', title: 'Error', text: `Could not update ${type}.` });
        }
    };

    const toggleExpand = (id: number) => {
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    return (
        <InternalShell activeDashboard="super_admin" subNav={SUPER_ADMIN_NAV}>
            <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faBuilding} className="me-2 text-primary" />Tenant & Org Management</h2>
            <p className="text-muted mb-4">Every institution and its clinics, across the platform</p>

            <div className="input-group mb-3" style={{ maxWidth: 400 }}>
                <span className="input-group-text"><FontAwesomeIcon icon={faSearch} /></span>
                <input className="form-control" placeholder="Search by name or email..." value={search}
                    onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} />
                <button className="btn btn-outline-primary" onClick={load}>Search</button>
            </div>

            <div className="card border-0 shadow-sm">
                <div className="card-body p-0">
                    {loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
                    ) : tenants.length === 0 ? (
                        <div className="text-center py-5 text-muted">No institutions found.</div>
                    ) : (
                        <table className="table table-hover align-middle mb-0">
                            <thead className="table-light">
                                <tr><th></th><th>Institution</th><th>Town</th><th>Clinics</th><th>Staff</th><th>Status</th><th>Action</th></tr>
                            </thead>
                            <tbody>
                                {tenants.map(t => (
                                    <React.Fragment key={t.id}>
                                        <tr style={{ cursor: 'pointer' }} onClick={() => toggleExpand(t.id)}>
                                            <td>{expanded.has(t.id) ? '▾' : '▸'}</td>
                                            <td><strong>{t.name}</strong><div className="small text-muted">{t.email}</div></td>
                                            <td>{t.town || '—'}</td>
                                            <td>{t.clinic_count}</td>
                                            <td>{t.staff_count}</td>
                                            <td><span className={`badge ${t.active ? 'bg-success' : 'bg-secondary'}`}>{t.active ? 'Active' : 'Suspended'}</span></td>
                                            <td onClick={(e) => e.stopPropagation()}>
                                                <button className={`btn btn-sm ${t.active ? 'btn-outline-danger' : 'btn-outline-success'}`} onClick={() => toggleTenant('institution', t.id, !t.active)}>
                                                    <FontAwesomeIcon icon={t.active ? faBan : faCheckCircle} className="me-1" />{t.active ? 'Suspend' : 'Reactivate'}
                                                </button>
                                            </td>
                                        </tr>
                                        {expanded.has(t.id) && t.clinics.map(c => (
                                            <tr key={`c-${c.id}`} style={{ background: '#f8fafb' }}>
                                                <td></td>
                                                <td className="ps-4"><FontAwesomeIcon icon={faHospital} className="me-2 text-muted" />{c.name}</td>
                                                <td colSpan={2}></td>
                                                <td>{c.staff_count}</td>
                                                <td><span className={`badge ${c.active ? 'bg-success' : 'bg-secondary'}`}>{c.active ? 'Active' : 'Suspended'}</span></td>
                                                <td>
                                                    <button className={`btn btn-sm ${c.active ? 'btn-outline-danger' : 'btn-outline-success'}`} onClick={() => toggleTenant('clinic', c.id, !c.active)}>
                                                        {c.active ? 'Suspend' : 'Reactivate'}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </InternalShell>
    );
};

export default TenantManagement;
